import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu as MenuIcon, X, Phone } from "lucide-react";
import { COMPANY } from "@/data/content";
import { NAV } from "@/constants/testIds";
import { GOLF_PARTNERSHIP_LIVE } from "@/config";

const NAV_LINKS = [
  { to: "/", label: "Home", testId: "nav-link-home" },
  { to: "/performance", label: "Performance & Prestige", testId: NAV.linkPerformance },
  { to: "/ev", label: "EV & Hybrid", testId: NAV.linkEv },
  { to: "/why", label: "Why OWRS", testId: NAV.linkWhy },
  { to: "/gallery", label: "Workshop", testId: "nav-link-gallery" },
  { to: "/sales", label: "Vehicles For Sale", testId: "nav-link-sales" },
  ...(GOLF_PARTNERSHIP_LIVE
    ? [{ to: "/golf", label: "Golf Partnership", testId: "nav-link-golf" }]
    : []),
  { to: "/contact", label: "Book In · Contact", testId: NAV.linkContact },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [stuckOpen, setStuckOpen] = useState(false);
  const [showStuck, setShowStuck] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setOpen(false);
    setStuckOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setShowStuck(window.scrollY > 320);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (to) => {
    setOpen(false);
    setStuckOpen(false);
    navigate(to);
  };

  return (
    <>
      {/* MAIN BILLBOARD HEADER */}
      <header
        data-testid="site-header"
        className="relative z-30"
        style={{ backgroundColor: "var(--owrs-surface)" }}
      >
        <div className="container-owrs pt-4 pb-2 sm:pt-6 sm:pb-3 md:pt-8 lg:pt-12">
          <div className="flex items-center justify-between gap-4 sm:gap-8">
            {/* LEFT — stacked vertical Performance / Prestige / EV */}
            <div className="flex-1 flex flex-col items-center gap-2 sm:gap-3 text-[11px] sm:text-[13px] tracking-[0.34em] uppercase text-[var(--owrs-text-muted)]">
              <Link
                to="/performance#porsche"
                data-testid="tagline-link-performance"
                className="hover:text-[var(--owrs-accent)] transition-colors text-center"
              >
                Performance
              </Link>
              <Link
                to="/performance#land-rover"
                data-testid="tagline-link-prestige"
                className="hover:text-[var(--owrs-accent)] transition-colors text-center"
              >
                Prestige
              </Link>
              <Link
                to="/ev"
                data-testid="tagline-link-ev"
                className="hover:text-[var(--owrs-accent)] transition-colors text-center"
              >
                EV
              </Link>
            </div>

            {/* CENTRE — logo scaled 2× from its centre so visual centre lines up with the side columns */}
            <Link to="/" data-testid={NAV.logo} className="shrink-0 relative z-10">
              <img
                src={COMPANY.logo}
                alt="OWRS Automotive Ltd"
                className="h-24 w-24 sm:h-32 sm:w-32 md:h-40 md:w-40 lg:h-52 lg:w-52 object-contain"
                style={{ transform: "scale(2)", transformOrigin: "center" }}
              />
            </Link>

            {/* RIGHT — stacked phone / book in / menu */}
            <div className="flex-1 flex flex-col items-center gap-2 sm:gap-3">
              <a
                href={`tel:${COMPANY.phoneTel}`}
                className="inline-flex items-center gap-2 text-xs sm:text-sm text-[var(--owrs-text-muted)] hover:text-[var(--owrs-accent)] transition-colors"
              >
                <Phone className="h-4 w-4" />
                <span className="hidden sm:inline">{COMPANY.phone}</span>
              </a>
              <button
                onClick={() => goTo("/contact")}
                data-testid={NAV.ctaBook}
                className="btn-primary !py-2 !px-4 text-xs sm:!py-2.5 sm:!px-5 sm:text-sm"
              >
                Book In
              </button>
              <button
                onClick={() => setOpen((o) => !o)}
                data-testid={NAV.mobileToggle}
                aria-label="Toggle menu"
                className="inline-flex items-center gap-2 text-xs sm:text-sm tracking-[0.18em] uppercase text-[var(--owrs-text-muted)] hover:text-[var(--owrs-accent)] transition-colors"
              >
                {open ? <X className="h-4 w-4" /> : <MenuIcon className="h-4 w-4" />}
                <span>{open ? "Close" : "Menu"}</span>
              </button>
            </div>
          </div>
        </div>

        {/* MENU DRAWER (billboard) */}
        {open && (
          <MenuDrawer onGoTo={goTo} pathname={location.pathname} />
        )}

        {/* Fade from header navy into the page below — applied on every page */}
        <div
          aria-hidden="true"
          className="absolute left-0 right-0 -bottom-24 sm:-bottom-32 h-24 sm:h-32 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, var(--owrs-surface) 0%, rgba(13,31,45,0.75) 35%, rgba(13,31,45,0.30) 75%, rgba(13,31,45,0) 100%)",
          }}
        />
      </header>

      {/* COMPACT STICKY HEADER */}
      <div
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          showStuck
            ? "translate-y-0 opacity-100"
            : "-translate-y-full opacity-0 pointer-events-none"
        }`}
      >
        <div className="bg-[rgba(13,31,45,0.95)] backdrop-blur-md border-b border-[var(--owrs-border)]">
          <div className="container-owrs flex items-center justify-between h-16">
            <button
              onClick={() => goTo("/")}
              className="flex items-center gap-3"
            >
              <img
                src={COMPANY.logo}
                alt=""
                className="h-10 w-10 object-contain mix-blend-lighten"
              />
              <span className="hidden sm:inline font-display text-sm font-semibold tracking-tight">
                OWRS Automotive
              </span>
            </button>
            <nav className="hidden lg:flex items-center gap-7 absolute left-1/2 -translate-x-1/2">
              {NAV_LINKS.slice(1).map((l) => {
                const active = location.pathname === l.to;
                return (
                  <button
                    key={l.to}
                    onClick={() => goTo(l.to)}
                    className={`text-xs tracking-wide transition-colors ${
                      active
                        ? "text-[var(--owrs-accent)]"
                        : "text-[var(--owrs-text-muted)] hover:text-[var(--owrs-text)]"
                    }`}
                  >
                    {l.label}
                  </button>
                );
              })}
            </nav>
            <div className="flex items-center gap-3">
              <a
                href={`tel:${COMPANY.phoneTel}`}
                className="hidden md:inline-flex items-center gap-2 text-xs text-[var(--owrs-text-muted)] hover:text-[var(--owrs-accent)]"
              >
                <Phone className="h-4 w-4" />
                {COMPANY.phone}
              </a>
              <button
                onClick={() => goTo("/contact")}
                className="btn-primary !py-2 !px-4 text-xs"
              >
                Book In
              </button>
              <button
                onClick={() => setStuckOpen((o) => !o)}
                aria-label="Toggle menu"
                className="lg:hidden inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-[var(--owrs-text-muted)] hover:text-[var(--owrs-accent)]"
              >
                {stuckOpen ? <X className="h-4 w-4" /> : <MenuIcon className="h-4 w-4" />}
              </button>
            </div>
          </div>
          {stuckOpen && <MenuDrawer onGoTo={goTo} pathname={location.pathname} />}
        </div>
      </div>
    </>
  );
}

function MenuDrawer({ onGoTo, pathname }) {
  return (
    <div className="border-t border-[var(--owrs-border)] bg-[var(--owrs-surface)] shadow-2xl">
      <div className="container-owrs py-6 sm:py-8">
        <nav className="grid sm:grid-cols-2 gap-1">
          {NAV_LINKS.map((l) => {
            const active = pathname === l.to;
            return (
              <button
                key={l.to}
                onClick={() => onGoTo(l.to)}
                data-testid={l.testId}
                className={`text-left py-4 px-2 border-b border-[var(--owrs-border)] transition-colors flex items-center justify-between group ${
                  active
                    ? "text-[var(--owrs-accent)]"
                    : "text-[var(--owrs-text)] hover:text-[var(--owrs-accent)]"
                }`}
              >
                <span className="font-display text-lg sm:text-xl uppercase">{l.label}</span>
                <span className="text-xs tracking-[0.2em] text-[var(--owrs-text-dim)] group-hover:text-[var(--owrs-accent)]">
                  {active ? "● Current" : "→"}
                </span>
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
